import { ErrorCode, FileRejection } from "react-dropzone";

import { TFunction } from "i18next";

import { ApiErrorCode, getApiErrorCode } from "../api/apiErrorHelper";
import {
  ACCEPTED_UPLOAD_EXTENSIONS_LABEL,
  ACCEPTED_UPLOAD_FILE_TYPES,
  MAX_UPLOAD_FILE_SIZE_BYTES,
} from "./acceptedFileTypes";

// Upload rejection/error notifications shared by the staff drop zone and the shared upload blocks.
// The `dropzone.upload.*` keys also exist in the internal app's own namespace: internal callers pass
// their app `t`, so the keys resolve there; shared callers pass the shared-namespace `t`.

function forSingleOrPlural<T>(items: T[], onSingle: (item: T) => void, onMultiple: (count: number) => void) {
  const [first] = items;
  if (first) {
    if (items.length === 1) {
      onSingle(first);
    } else {
      onMultiple(items.length);
    }
  }
}

export function notifyRejections(rejectedFiles: FileRejection[], showError: (message: string) => void, t: TFunction) {
  const hasErrorCode = (rejection: FileRejection, code: string) => rejection.errors.some((e) => e.code === code);

  const tooLarge = rejectedFiles.filter((r) => hasErrorCode(r, ErrorCode.FileTooLarge));
  const invalidType = rejectedFiles.filter((r) => hasErrorCode(r, ErrorCode.FileInvalidType));

  forSingleOrPlural(
    tooLarge,
    ({ file }) => showError(t("dropzone.upload.fileTooLarge", { fileName: file.name })),
    (count) => showError(t("dropzone.upload.fileTooLargeMultiple", { count }))
  );
  forSingleOrPlural(
    invalidType,
    ({ file }) => showError(t("dropzone.upload.invalidFileType", { fileName: file.name, types: ACCEPTED_EXTENSIONS })),
    (count) => showError(t("dropzone.upload.invalidFileTypeMultiple", { count, types: ACCEPTED_EXTENSIONS }))
  );
}

export async function getUploadErrorMessage(error: unknown, fileName: string, t: TFunction): Promise<string> {
  switch (await getApiErrorCode(error)) {
    case ApiErrorCode.FileTooLarge:
      return t("dropzone.upload.fileTooLarge", { fileName });
    case ApiErrorCode.InvalidFileType:
      return t("dropzone.upload.invalidFileType", { fileName, types: ACCEPTED_EXTENSIONS });
    case ApiErrorCode.EmailAttachmentConversionUnsupported:
      return t("dropzone.upload.attachmentConversionFailed", { fileName });
    case ApiErrorCode.UploadConversionFailed:
      return t("dropzone.upload.conversionFailed", { fileName });
    default:
      return t("dropzone.upload.error", { fileName });
  }
}

// File-type/size limits re-exposed under the drop zone's existing names so lifted upload blocks
// keep their identifiers.
export const DROPZONE_MAX_FILE_SIZE = MAX_UPLOAD_FILE_SIZE_BYTES;

export const DROPZONE_ACCEPTED_FILES = ACCEPTED_UPLOAD_FILE_TYPES;

export const ACCEPTED_EXTENSIONS = ACCEPTED_UPLOAD_EXTENSIONS_LABEL;
